import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../context/TranslationContext';
import { getAdminProducts, deleteProduct } from '../../services/api';
import { Product } from '../../types';
import ProductModal from './components/ProductModal';
import './ProductListPage.css';

const ProductListPage: React.FC = () => {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [currentProduct, setCurrentProduct] = useState<Product | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Fetch products on component mount
  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Clear success message after 3 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const fetchProducts = async () => {
    if (!token) return;

    try {
      setLoading(true);
      setError(null);
      const data = await getAdminProducts(token);
      
      if (data.length > 0) {      
        // Check if we have id or _id
        if (data[0].id !== undefined) {
          
          // Map the data to ensure _id is set
          const mappedData = data.map(product => ({
            ...product,
            _id: product.id ? product.id.toString() : undefined // Ensure _id is set from id
          }));
          
          // Sort products by ID to maintain consistent order
          const sortedData = [...mappedData].sort((a, b) => {
            const aId = a.id || parseInt(a._id || '0');
            const bId = b.id || parseInt(b._id || '0');
            return aId - bId;
          });
          
          setProducts(sortedData);
        } else if (data[0]._id !== undefined) {
          
          // Sort products by ID to maintain consistent order
          const sortedData = [...data].sort((a, b) => {
            const aId = parseInt(a._id || '0');
            const bId = parseInt(b._id || '0');
            return aId - bId;
          });
          
          setProducts(sortedData);
        } else {
          console.error('Product has neither id nor _id property');
          setError(t('admin.common.error'));
        }
      } else {
        setProducts(data);
      }
    } catch (err) {
      console.error('Error fetching products:', err);
      setError(t('admin.common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleAddProduct = () => {
    setCurrentProduct(null);
    setShowModal(true);
  };

  const handleEditProduct = (product: Product) => {    
    // Validate product ID
    if (!product) {
      console.error('Cannot edit product: Invalid product');
      setError(t('admin.common.error'));
      return;
    }
    
    // Check for _id or id
    const productId = product._id || (product as any).id?.toString();
    
    if (!productId) {
      console.error('Cannot edit product: Missing ID');
      console.log('Product object keys:', Object.keys(product));
      setError(t('admin.common.error'));
      return;
    }
    
    // Ensure product has _id property
    const productWithId = {
      ...product,
      _id: productId
    };
    
    console.log('ProductListPage: Editing product with ID:', productId);
    setCurrentProduct(productWithId);
    setShowModal(true);
  };

  const handleDeleteProduct = async (id: string | number) => {
    // Convert id to string if it's a number
    const idString = id?.toString();
    
    if (!token) {
      console.error('Cannot delete product: No token available');
      return;
    }
    
    if (!idString) {
      console.error('Cannot delete product: Missing ID');
      return;
    }

    try {
      setIsDeleting(true);
      setDeleteId(idString);
      
      // Log the ID to verify it's valid
      console.log('Deleting product with ID:', idString);
      
      await deleteProduct(idString, token);
      
      // Filter products based on either _id or id
      setProducts(products.filter(product => {
        const productId = product._id || (product as any).id?.toString();
        return productId !== idString;
      }));
      
      setSuccessMessage(t('admin.products.productDeleted'));
    } catch (err) {
      console.error('Error deleting product:', err);
      setError(t('admin.common.error'));
    } finally {
      setIsDeleting(false);
      setDeleteId(null);
    }
  };

  const handleModalClose = (productUpdated: boolean = false) => {
    setShowModal(false);
    if (productUpdated) {
      // Fetch products again to get the updated list
      fetchProducts();
    }
  };

  if (loading && products.length === 0) {
    return <div className="admin-loading">{t('admin.common.loading')}</div>;
  }

  return (
    <div className="product-list-page">
      <div className="page-header">
        <h1>{t('admin.products.title')}</h1>
        <button 
          className="btn btn-primary add-button" 
          onClick={handleAddProduct}
        >
          {t('admin.products.addProduct')}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      {products.length === 0 ? (
        <div className="no-data-message">{t('admin.common.noData')}</div>
      ) : (
        <div className="product-table-container">
          <table className="product-table">
            <thead>
              <tr>
                <th>{t('admin.products.productName')}</th>
                <th>{t('admin.products.price')}</th>
                <th>{t('admin.products.available')}</th>
                <th>{t('admin.products.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {products.map(product => (
                <tr key={product._id}>
                  <td>
                    <div className="product-name-cell">
                      {product.imageUrl && (
                        <img 
                          src={product.imageUrl} 
                          alt={product.name} 
                          className="product-thumbnail" 
                        />
                      )}
                      <span>{product.name}</span>
                    </div>
                  </td>
                  <td>{product.price.toLocaleString('hu-HU')} Ft</td>
                  <td>
                    <span className={`availability-badge ${product.available ? 'available' : 'unavailable'}`}>
                      {product.available ? t('admin.common.yes') : t('admin.common.no')}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="btn-action btn-edit" 
                        onClick={() => handleEditProduct(product)}
                      >
                        {t('admin.common.edit')}
                      </button>
                      <button 
                        className="btn-action btn-delete" 
                        onClick={() => handleDeleteProduct(product._id || (product as any).id)}
                        disabled={isDeleting && deleteId === (product._id || (product as any).id?.toString())}
                      >
                        {isDeleting && deleteId === (product._id || (product as any).id?.toString())
                          ? '...' 
                          : t('admin.common.delete')
                        }
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <ProductModal 
          product={currentProduct} 
          onClose={handleModalClose} 
        />
      )}
    </div>
  );
};

export default ProductListPage;
